<template>
  <div>
    <my-header></my-header>
    <div class="container-fluid">
      <h1>M3 Levels</h1>

      <period-selector
        :audienceId="audienceId"
        :startLevel="startLevel"
        :endLevel="endLevel"
      />

      <div class="row">
        <div class="plot-cont col-md-12">
          <Plotly
            v-if="plotly_data !== null"
            :data="plotly_data"
            :layout="plotly_layout"
          />
        </div>
      </div>

      <div class="row">
        <nav class="col-md-2">
          <div v-if="sequences !== null" class="sidebar sticky-top">

            <div class="sidebar-group mb-3">
              <h6 class="text-muted mb-1">Последовательности уровней</h6>
              <sequence-selector
                :sequences="sequences"
                :currentSequenceId="currentSequenceId"
                @sequence-selected="selectSequence"
              />
            </div>

            <div v-if="currentSequence !== null" class="sidebar-group mb-2">
                <h6 class="text-muted mb-0">Фильтры</h6>
                <m3-levels-filter
                  :audienceIds="audiences"
                  :audienceId="audienceId"
                  :startLevel="startLevel"
                  :endLevel="endLevel"
                  size="sm"
                  v-on:loading-selected="handleFilters"
                  v-on:error="handleFilterError"
                />
            </div>
            <div class="sidebar-group mb-2">
                <b-button @click="downloadCSV" size="sm">Скачать CSV</b-button>
            </div>

          </div>
        </nav>

        <main class="col-md-10">
          <div v-if="currentSequence === null">
            &larr; Выберите линейку уровней.
          </div>

          <revisions-view
            v-if="currentSequence !== null && levels !== null && levels.length > 0"
            :levels="levels"
            :minPassRateConfs="minPassRateConfs"
          />

          <loading-indicator
            v-if="isLoading"
          />
        </main>

      </div>

      <hr>

      <div class="setting-panel">
        <router-link :to="{name: 'm3_manager'}">
          Управление
        </router-link>
      </div>

    </div>
  </div>
</template>

<script>
import { HTTP } from '../../http-common';
import notifier from '../../notifier';
import { saveAs } from 'file-saver';

/**
 * @typedef { import('../../model/m3level.js').LevelObject } LevelObject
 */

import { MAIN_SEQUENCE_PROTO_ID } from '../../model/m3level.js';
import { SequenceFragmentId     } from '../../model/m3level.js';

import { LevelGroupLoader       } from '../../model/m3level-group-loader.js';

import { compareProtoIdsByAlias } from '../../util.js';
import { addParamToQueryObject  } from '../../util.js';

import MyHeader from '../Header.vue';
import PeriodSelector from './PeriodSelector.vue';
import LoadingIndicator from '../LoadingIndicator.vue';
import SequenceSelector from './SequenceSelector.vue';
import M3LevelsFilter from './M3LevelsFilter.vue';
import RevisionsView from './RevisionsView.vue';
import { Plotly } from 'vue-plotly';

const BLOCK_LEVELS_AMOUNT = 100;

export default {
  name: 'Levels',

  components: {
    MyHeader,
    PeriodSelector,
    LoadingIndicator,
    SequenceSelector,
    M3LevelsFilter,
    RevisionsView,
    Plotly,
  },

  props: {
    sequenceFragmentId: {
      type: SequenceFragmentId,
      default: null,
    },

    sequenceID: {
      type: Number,
      default: null,
    },

    audienceId: {
      type: Number,
      default: null,
    },

    startLevel: {
      type: Number,
      default: null,
    },

    endLevel: {
      type: Number,
      default: null,
    },

    periodName: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      /** @type {?LevelGroupLoader} */
      levelGroupLoader: null,

      isLoading: false,

      /** @type {?Symbol} */
      loadingId: null,

      /** @type {?SequenceInfo[]} */
      sequences: null,

      sequenceOptions: null,

      /** @type {?SequenceInfo} */
      currentSequence: null,

      /** @type {?LevelObject[]} */
      levels: null,

      /** @type {?Object[]} */
      plotly_data: null,

      plotly_layout: {
        title: 'Levels stats',
        height: 460,
      },
    };
  },

  computed: {
    /** @returns {MinPassRate[]} */
    minPassRateConfs() {
      return this.currentSequence.minPassRate;
    },

    /** @returns {?number[]} */
    audiences() {
      if(this.currentSequence === null) {
        return null;
      }
      if(!('audiences' in this.currentSequence)) {
        return null;
      }

      const auds = this.currentSequence.audiences;

      if(auds === undefined) {
        return null;
      }
      if(auds === null) {
        return null;
      }
      if(auds.length === 0) {
        return null;
      }
      return auds;
    },

    /** @returns {?number} */
    currentSequenceId() {
      const currentSequence = this.currentSequence;
      if(currentSequence === null) {
        return null;
      }
      return currentSequence.id;
    },
  },

  watch: {
    sequenceID() {
      this.updateCurrentSequence();
    },

    currentSequence() {
      this.resetSequenceData();
    },

    periodName() {
      this.resetSequenceData();
    },
  },

  created() {
    this.levelGroupLoader = new LevelGroupLoader();
    this.loadSequences();
  },

  beforeDestroy() {
    this.loadingId = null;
  },

  methods: {
    resetSequenceData() {
      this.cancelLevelsLoading();
      this.levels = null;
      this.plotly_data = null;
    },

    loadSequences() {
      notifier.deleteSenderNotifications(this);

      const url = '/m3/sequence_infos';
      HTTP.get(url)
      .then(response => {
        /** @type {SequenceInfo[]} */
        const sequences = response.data.data;

        sequences.sort(compareSequences);

        this.sequences = sequences;
        this.sequenceOptions = createSequenceOptions(sequences);

        this.updateCurrentSequence();
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },

    updateCurrentSequence() {
      if(this.currentSequence !== null && this.currentSequence.id === this.sequenceID) {
        return;
      }

      if(this.sequenceID === undefined) {
        this.currentSequence = null;
        return;
      }

      const sequence = findSequenceById(this.sequences, this.sequenceID);
      if(sequence !== null) {
        this.currentSequence = sequence;
      }
    },

    /**
     * @param {number} sequenceId
     */
    selectSequence(sequenceId) {
      const queryObject = {};
      addParamToQueryObject(queryObject, 'period', this.periodName);

      this.$router.push({name: 'sequence_levels', params: {sequence_id: sequenceId}, query: queryObject});
    },

    /**
     * @param {?number} audienceId
     * @param {?number} startLevel
     * @param {?number} endLevel
     */
    handleFilters(audienceId, startLevel, endLevel) {
      this.goToSelectedPage(audienceId, startLevel, endLevel);
      this.loadLevels(this.currentSequence, audienceId, startLevel, endLevel, this.periodName);
    },

    /**
     * @param {?number} audienceId
     * @param {?number} startLevel
     * @param {?number} endLevel
     */
    goToSelectedPage(audienceId, startLevel, endLevel) {
      if(this.isOnTargetPage(audienceId, startLevel, endLevel)) {
        return;
      }

      const queryObject = {};
      addParamToQueryObject(queryObject, 'audience',    audienceId);
      addParamToQueryObject(queryObject, 'start_level', startLevel);
      addParamToQueryObject(queryObject, 'end_level',   endLevel);
      addParamToQueryObject(queryObject, 'period',      this.periodName);

      this.$router.push({name: 'sequence_levels', query: queryObject});
    },

    /**
     * @param {?number} audienceId
     * @param {?number} startLevel
     * @param {?number} endLevel
     */
    isOnTargetPage(audienceId, startLevel, endLevel) {
      return (this.audienceId === audienceId) &&
             (this.startLevel === startLevel) &&
             (this.endLevel   === endLevel);
    },

    /**
     * @param {SequenceInfo} sequence
     * @param {?number} audienceId
     * @param {?number} startLevel
     * @param {?number} endLevel
     * @param {?string} periodName
     */
    loadLevels(sequence, audienceId, startLevel, endLevel, periodName) {
      notifier.deleteSenderNotifications(this);

      const globalFirstLevelNumber = (startLevel !== null) ? startLevel : 1;
      const globalEndLevelNumber   = (endLevel   !== null) ? endLevel   : sequence.levelsAmount;

      if(globalFirstLevelNumber > globalEndLevelNumber) {
        notifier.showError(
          `Invalid level range ${globalFirstLevelNumber} - ${globalEndLevelNumber}. Start level must be less or equal to end level.`,
          {sender: this}
        );
        return;
      }

      let firstLevelNumber = globalFirstLevelNumber;

      const currentLoadingId = Symbol('loading id');
      this.loadingId = currentLoadingId;

      this.isLoading = true;
      this.levels = null;
      this.plotly_data = null;

      const loadCurrentLevelsBlock = function() {
        const endLevelNumber = getBlockEndLevelNumberLimited(firstLevelNumber, globalEndLevelNumber);

        this.loadLevelsBlock(
          sequence.id,
          audienceId,
          firstLevelNumber,
          endLevelNumber,
          periodName,
          onLevelsBlockLoaded.bind(this),
          onLoadingError.bind(this)
        );
      }.bind(this);

      loadCurrentLevelsBlock();

      function onLevelsBlockLoaded(levelsBlock) {
        if(currentLoadingId !== this.loadingId) {
          return;
        }

        if(this.levels === null) {
          this.levels = [];
        }

        for(const level of levelsBlock) {
          this.levels.push(level);
        }

        if(globalEndLevelNumber === null) {
          if(levelsBlock.length === 0) {
            this.onAllBlocksLoaded();
          } else {
            firstLevelNumber = getNextBlockBeginLevelNumber(firstLevelNumber);
            loadCurrentLevelsBlock();
          }
          return;
        }

        if(globalEndLevelNumber !== null) {
          const endLevelNumber = getBlockEndLevelNumberLimited(firstLevelNumber, globalEndLevelNumber);
          if(endLevelNumber === globalEndLevelNumber) {
            this.onAllBlocksLoaded();
          } else {
            firstLevelNumber = getNextBlockBeginLevelNumber(firstLevelNumber);
            loadCurrentLevelsBlock();
          }
          return;
        }
      }

      function onLoadingError() {
        if(currentLoadingId !== this.loadingId) {
          return;
        }
        this.isLoading = false;
      }

      /*
      const url = `/m3/sequences/${sequenceID}/levels`;

      var urlParams = new URLSearchParams();
      for(const name in params) {
        if (params.hasOwnProperty(name) && params[name] !== null) {
          urlParams.append(name, params[name])
        }
      }
      const urlParamsString = urlParams.toString()
      var resURL = url
      if(urlParamsString.length > 0){
        resURL += `?${urlParamsString}`
      }

      HTTP.get(resURL)
      .then(response => {
        this.isLoading = false;

        this.levels = response.data.data;
        this.fillPlotData(response.data.data);
      })
      .catch(e => {
        this.isLoading = false;
        notifier.showError(e.response.data, {sender: this});
      });
      */
    },

    cancelLevelsLoading() {
      this.loadingId = null;
      this.isLoading = false;
    },

    onAllBlocksLoaded() {
      this.fillPlotData(this.levels);
      this.isLoading = false;
    },

    /**
     * @param {number} sequenceId
     * @param {?number} audienceId
     */
    loadLevelsBlock(sequenceId, audienceId, beginLevelNumber, endLevelNumber, periodName, onLoad, onError) {
      const params = {
        level_from: beginLevelNumber,
        level_to:   endLevelNumber,
      };

      if(audienceId !== null) {
        params.audience = audienceId;
      }

      if(periodName !== null) {
        params.period = periodName;
      }

      HTTP.get(
        `/m3/sequences/${sequenceId}/levels`,
        {
          params: params,
        }
      )
      .then(response => {
        const levelsBlock = response.data.data;
        onLoad(levelsBlock);
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
        onError();
      });
    },

    handleFilterError(error) {
      notifier.showError(error, {sender: this});
    },

    downloadCSV() {
      const url = `/m3/sequences/${this.currentSequence.id}/levels?format=csv`;
      HTTP.get(url, {responseType: 'blob'})
      .then(response => {
        saveAs(new Blob([response.data]), 'sequence_' + this.currentSequence.id + '.csv');
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },

    /**
     * @param {LevelObject[]} data
     */
    fillPlotData(data) {
      var plots = {
        averageTurnAmount: {
          name: `Average turns`,
          type: 'scatter',
          mode: 'lines+markers',
          x: [],
          y: [],
        },
        averageWinnerTurnAmount: {
          name: `Average winner turns`,
          type: 'scatter',
          mode: 'lines+markers',
          x: [],
          y: [],
        },
        FailRateWithBoosters: {
          name: `Fail rate w/ boosters`,
          type: 'scatter',
          mode: 'lines+markers',
          x: [],
          y: [],
        },
        FailRateWithoutBoosters: {
          name: `Fail rate w/o boosters`,
          type: 'scatter',
          mode: 'lines+markers',
          x: [],
          y: [],
        },
        AverageAttemptsBeforeWin: {
          name: `Avg. fails before win`,
          type: 'scatter',
          mode: 'lines+markers',
          x: [],
          y: [],
        },
        AverageUsedMoneyTotal: {
          name: `Avg. used money total`,
          type: 'scatter',
          mode: 'lines+markers',
          x: [],
          y: [],
        },
        AverageFailsWithBoosters: {
          name: `Avg. fails w/ boosters`,
          type: 'scatter',
          mode: 'lines+markers',
          x: [],
          y: [],
        },
        AverageFailsWithoutBoosters: {
          name: `Avg. fails w/o boosters`,
          type: 'scatter',
          mode: 'lines+markers',
          x: [],
          y: [],
        },
        BoosterUsageAttemptsWithBoosters: {
          name: `Booster usage: attempts w/ boosters`,
          type: 'scatter',
          mode: 'lines+markers',
          x: [],
          y: [],
        },
        payerConversion: {
          name: `Conversion into payer`,
          type: 'scatter',
          mode: 'lines+markers',
          x: [],
          y: [],
        },
        PassRate: {
          name: `Pass rate`,
          type: 'scatter',
          mode: 'lines+markers',
          x: [],
          y: [],
        },
        MonetizationQuality: {
          name: `Monetization quality`,
          type: 'scatter',
          mode: 'lines+markers',
          x: [],
          y: [],
        },
        sacrificeRatio: {
          name: `Sacrifice ratio`,
          type: 'scatter',
          mode: 'lines+markers',
          x: [],
          y: [],
        },
        CoolnessFactor: {
          name: `Coolness factor`,
          type: 'scatter',
          mode: 'lines+markers',
          x: [],
          y: [],
        },
      };

      data.forEach(level => {
        level.revisions.forEach(revision => {
          const levelNumStr = `level#${revision.id.number}`;

          plots.averageTurnAmount.x.push(levelNumStr);
          plots.averageTurnAmount.y.push(revision.stat.averageTurnAmount);

          plots.averageWinnerTurnAmount.x.push(levelNumStr);
          plots.averageWinnerTurnAmount.y.push(revision.stat.averageWinnerTurnAmount);

          plots.FailRateWithBoosters.x.push(levelNumStr);
          plots.FailRateWithBoosters.y.push(revision.stat.FailRate.WithBoosters);
          plots.FailRateWithoutBoosters.x.push(levelNumStr);
          plots.FailRateWithoutBoosters.y.push(revision.stat.FailRate.WithoutBoosters);

          plots.AverageAttemptsBeforeWin.x.push(levelNumStr);
          plots.AverageAttemptsBeforeWin.y.push(revision.stat.averageFailAmountBeforeFirstWin);

          plots.AverageUsedMoneyTotal.x.push(levelNumStr);
          plots.AverageUsedMoneyTotal.y.push(revision.stat.averageUsedMoneyTotal);

          plots.AverageFailsWithBoosters.x.push(levelNumStr);
          plots.AverageFailsWithBoosters.y.push(revision.stat.AverageFails.WithBoosters);
          plots.AverageFailsWithoutBoosters.x.push(levelNumStr);
          plots.AverageFailsWithoutBoosters.y.push(revision.stat.AverageFails.WithoutBoosters);
          plots.BoosterUsageAttemptsWithBoosters.x.push(levelNumStr);
          plots.BoosterUsageAttemptsWithBoosters.y.push(revision.stat.extraUsageRate);
          plots.payerConversion.x.push(levelNumStr);
          plots.payerConversion.y.push(revision.stat.payerConversion);
          plots.PassRate.x.push(levelNumStr);
          plots.PassRate.y.push(revision.stat.PassRate);
          plots.MonetizationQuality.x.push(levelNumStr);
          plots.MonetizationQuality.y.push(revision.stat.MonetizationQuality);

          plots.sacrificeRatio.x.push(levelNumStr);
          plots.sacrificeRatio.y.push(revision.stat.sacrificeRatio);

          plots.CoolnessFactor.x.push(levelNumStr);
          plots.CoolnessFactor.y.push(revision.stat.CoolnessFactor);
        });
      });

      const plotsData = [
        plots.averageTurnAmount,
        plots.averageWinnerTurnAmount,
        plots.FailRateWithBoosters,
        plots.FailRateWithoutBoosters,
        plots.AverageFailsWithBoosters,
        plots.AverageFailsWithoutBoosters,
        plots.AverageAttemptsBeforeWin,
        plots.AverageUsedMoneyTotal,
        plots.PassRate,
        plots.sacrificeRatio,
        plots.payerConversion,
        plots.MonetizationQuality,
        plots.BoosterUsageAttemptsWithBoosters,
        plots.CoolnessFactor,
      ];
      this.plotly_data = plotsData;
    },
  },
}

/**
 * @typedef {Object} SequenceInfo
 * @property {number} id
 * @property {string} name
 * @property {number} levelsAmount
 * @property {number[]} [audiences]
 * @property {MinPassRate[]} minPassRate
 * 
 * @typedef {Object} MinPassRate
 * @property {number} difficulty
 */

/**
 * @param {SequenceInfo[]} sequences
 * @param {number} sequenceId
 * @returns {?SequenceInfo}
 */
function findSequenceById(sequences, sequenceId) {
  for(const sequence of sequences) {
    if(sequence.id === sequenceId) {
      return sequence;
    }
  }
  return null;
}

function getBlockEndLevelNumberLimited(beginLevelNumber, globalEndLevelNumber) {
  const blockLastLevelNumber = getBlockLastLevelNumber(beginLevelNumber);

  if(globalEndLevelNumber === null) {
    return blockLastLevelNumber;
  }
  if(blockLastLevelNumber < globalEndLevelNumber) {
    return blockLastLevelNumber;
  }
  return globalEndLevelNumber;
}

function getNextBlockBeginLevelNumber(beginLevelNumber) {
  const blockLastLevelNumber = getBlockLastLevelNumber(beginLevelNumber);
  return blockLastLevelNumber + 1;
}

function getBlockLastLevelNumber(beginLevelNumber) {
  const beginLevelIndex = beginLevelNumber - 1;
  const blockFirstLevelIndex = Math.floor(beginLevelIndex / BLOCK_LEVELS_AMOUNT) * BLOCK_LEVELS_AMOUNT;
  const blockFirstLevelNumber = blockFirstLevelIndex + 1;
  const blockLastLevelNumber = blockFirstLevelNumber + BLOCK_LEVELS_AMOUNT - 1;
  return blockLastLevelNumber;
}

function compareSequences(sequenceA, sequenceB) {
  if(sequenceA.id === sequenceB.id) {
    // Impossible case in normal execution.
    return 0;
  }

  if(sequenceA.id === MAIN_SEQUENCE_PROTO_ID) {
    return -1;
  }
  if(sequenceB.id === MAIN_SEQUENCE_PROTO_ID) {
    return 1;
  }

  return compareProtoIdsByAlias(sequenceA.id, sequenceB.id);
}

// options for component b-form-select
function createSequenceOptions(sequences) {
  const options = [];

  for(const sequence of sequences) {
    const option = {
      value: sequence.id,
      text:  sequence.name,
    };
    options.push(option);
  }

  return options;
}
</script>

<style scoped>
.plot-cont {
  min-height: 460px;
}

.setting-panel {
  margin-bottom: 1em;
}
</style>
